import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import type { Car } from "@/reducers/carsSlice"

export const plateKeyOf = (car: Car) => car.plates || car.label

export const isGoneFromGps = (car: Car) => car.lastSeen === "long_ago"

// With one brand every row would carry the same pill, so sources are named
// only when there are several.
export const hasSeveralBrands = (cars: Car[]) =>
  new Set(cars.map((car) => car.gpsBrand).filter(Boolean)).size > 1

// last_seen says whether the provider still lists the car, not whether the
// box sends positions, so the copy claims no more than that.
const SourcePill = ({ brand, gone }: { brand: string; gone: boolean }) => {
  const { t } = useTranslation()
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-0.5 text-xs",
              gone && "text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                gone
                  ? "border border-muted-foreground"
                  : "bg-emerald-500 ring-2 ring-emerald-500/20",
              )}
            />
            {gone ? t("fleet.vehicleName.sourceGone", { brand }) : brand}
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">
          {gone
            ? t("fleet.vehicleName.sourceGoneTooltip", { brand })
            : t("fleet.vehicleName.sourceListedTooltip", { brand })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// The same plates can come from two providers, one a box that went dead, so
// `brand` names the source. Leave it out when every car shares one.
export const VehicleName = ({ car, brand }: { car: Car; brand?: string }) => {
  const { t } = useTranslation()
  const gone = isGoneFromGps(car)
  return (
    <span className="inline-flex min-w-0 items-center gap-2.5">
      <span
        title={car.label}
        className={cn(
          "truncate",
          // A fixed width lines the pills up in one column.
          brand && "w-24 shrink-0",
          gone && "text-muted-foreground",
        )}
      >
        {plateKeyOf(car)}
      </span>
      {brand ? (
        <SourcePill brand={brand} gone={gone} />
      ) : (
        gone && (
          <span
            title={t("fleet.vehicleName.goneTitle")}
            className="shrink-0 text-xs text-amber-700 dark:text-amber-400"
          >
            {t("fleet.vehicleName.gone")}
          </span>
        )
      )}
    </span>
  )
}
